import { useEffect, useRef } from 'react';
import { buildYourTourApiUrl, fetchYourTourApi } from '../utils/ApiHelpers';
import { resetDataVersionsCache } from '../utils/dataVersions';

// How often to poll the heartbeat endpoint, in milliseconds, per platform.
//
// `yt` is the only platform where a human is watching for a status change to
// land within seconds (sales staff moving a residence while a prospect is in
// front of them). Every other platform is a display surface, so a minute is
// ample — and the heartbeat is the single largest line of the Pantheon
// pageview bill, so the difference is not academic.
const HEARTBEAT_INTERVAL_MS_BY_PLATFORM = {
  yt: 5000,
  kiosk: 60000,
};

const DEFAULT_HEARTBEAT_INTERVAL_MS = 60000;

export const getHeartbeatIntervalMs = ( platform ) => (
  HEARTBEAT_INTERVAL_MS_BY_PLATFORM[platform] ?? DEFAULT_HEARTBEAT_INTERVAL_MS
);

// One poller per community+platform, shared by every subscriber.
//
// Both useMapData and a mounted residence detail want the same heartbeat URL
// for the same data; without this they each ran a private setInterval and
// doubled the request rate to that URL. Keyed by platform and slug because a
// different platform polls on a different interval.
const pollers = new Map();

const pollerKey = ( urlSlug, platform ) => `${platform}|${urlSlug}`;

const createPoller = ( urlSlug, platform ) => {
  const subscribers = new Set();
  const versions = {
    mapStructure: null,
    mapOccupancy: null,
    residence: null,
    communityConfig: null,
  };
  let hasBaseline = false;
  let intervalId = null;
  let isTornDown = false;

  const notify = ( name ) => {
    subscribers.forEach( ( handlers ) => {
      const handler = handlers[name];
      if ( handler ) {
        handler();
      }
    } );
  };

  const check = async () => {
    try {
      const url = buildYourTourApiUrl(
        `/your_tour_api/heartbeat/${urlSlug}`,
        {},
        platform,
      );
      const data = await fetchYourTourApi( url, platform );

      if ( !data || isTornDown ) {
        return;
      }

      // The first response is a baseline: record it without firing callbacks.
      if ( !hasBaseline ) {
        hasBaseline = true;
        versions.mapStructure = data.mapStructure;
        versions.mapOccupancy = data.mapOccupancy;
        versions.residence = data.residence;
        versions.communityConfig = data.communityConfig;
        return;
      }

      // Any counter moving means the server-side data changed, so the
      // data-versions hashes this page cached at load are stale. Drop them
      // before notifying, or the refetch a subscriber is about to fire carries
      // the page-load ?v= hash and the CDN can serve back the very payload the
      // heartbeat fired to replace.
      const changed = data.mapStructure !== versions.mapStructure
        || data.mapOccupancy !== versions.mapOccupancy
        || data.residence !== versions.residence
        || data.communityConfig !== versions.communityConfig;

      if ( changed ) {
        resetDataVersionsCache( urlSlug );
      }

      // Map structure changed (rare — SVG, levels, etc.).
      if ( data.mapStructure !== versions.mapStructure ) {
        versions.mapStructure = data.mapStructure;
        notify( 'onMapStructureChange' );
      }

      // Map occupancy changed (frequent — status changes).
      if ( data.mapOccupancy !== versions.mapOccupancy ) {
        versions.mapOccupancy = data.mapOccupancy;
        notify( 'onMapOccupancyChange' );
      }

      // Residence version changed (status or residents).
      if ( data.residence !== versions.residence ) {
        versions.residence = data.residence;
        notify( 'onResidenceVersionChange' );
      }

      // Community config changed (feature flags, colors, etc.).
      if ( data.communityConfig !== versions.communityConfig ) {
        versions.communityConfig = data.communityConfig;
        notify( 'onCommunityConfigChange' );
      }
    } catch ( error ) {
      // Silently ignore heartbeat errors — this is a best-effort mechanism.
      // The existing version-check on page load still works as fallback.
    }
  };

  const startPolling = () => {
    if ( intervalId ) {
      clearInterval( intervalId );
    }
    intervalId = setInterval( check, getHeartbeatIntervalMs( platform ) );
  };

  const stopPolling = () => {
    if ( intervalId ) {
      clearInterval( intervalId );
      intervalId = null;
    }
  };

  const isVisible = () => (
    // No `document` (SSR, a test environment without jsdom) means nothing is
    // hidden, so treat it as visible rather than never polling at all.
    typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );

  const handleVisibilityChange = () => {
    if ( isVisible() ) {
      // Tab became visible — check immediately, then resume polling.
      check();
      startPolling();
    } else {
      // Tab became hidden — stop polling to save resources.
      stopPolling();
    }
  };

  const teardown = () => {
    isTornDown = true;
    stopPolling();
    document.removeEventListener( 'visibilitychange', handleVisibilityChange );
  };

  // Take the baseline whatever the visibility state — a subscriber that mounts
  // hidden still needs versions to compare against when the tab comes forward.
  check();

  // Only start the timer if the tab is visible NOW. `visibilitychange` fires on
  // a *transition*, so a page that loads into an already-hidden tab (a kiosk
  // opened in a background tab, a restored session, a prerender) never received
  // one, and polled hidden forever — the exact case the pause exists to avoid.
  // Measured 2026-09-05 on release/1.16: a kiosk tab hidden from load kept
  // polling indefinitely, once a minute, because Chrome clamps background
  // timers to 60s rather than because we intended it.
  if ( isVisible() ) {
    startPolling();
  }

  document.addEventListener( 'visibilitychange', handleVisibilityChange );

  return { subscribers, teardown };
};

/**
 * useHeartbeat - Subscribes to the shared heartbeat poller for a community.
 *
 * The heartbeat endpoint returns four version counters:
 * - mapStructure: Changes when map structure changes (SVG, levels, etc.)
 * - mapOccupancy: Changes when residence status changes
 * - residence: Changes when residence status or residents change
 * - communityConfig: Changes when the community node is updated (feature
 *   flags, colors, other community-level settings)
 *
 * Every subscriber for the same community and platform shares one interval and
 * one request per tick, at `getHeartbeatIntervalMs( platform )` — 5s on `yt`,
 * 60s everywhere else. Polling is visibility-aware in both directions: it does
 * not start at all while the tab is hidden — including when the page loads
 * straight into a hidden tab, which fires no `visibilitychange` — and it pauses
 * when a visible tab is backgrounded. Becoming visible always runs an immediate
 * check before resuming the timer, so what the tab shows on its first painted
 * frame is never older than the moment it came forward.
 *
 * Callers should pass `enabled: platformShowOccupancy` (ANDed with their own
 * conditions): the counters this hook watches only drive occupancy and
 * residence status, so a platform that does not render those has nothing to
 * poll for.
 *
 * @param {string} urlSlug - The community URL slug
 * @param {string} platform - The current platform (yt, kiosk, siteSee, etc.)
 * @param {object} options
 * @param {Function} options.onMapStructureChange - Called when map structure version changes
 *   (triggers full map refetch)
 * @param {Function} options.onMapOccupancyChange - Called when map occupancy version changes
 *   (triggers lightweight occupancy-only refetch)
 * @param {Function} options.onResidenceVersionChange - Called when residence version changes
 *   (triggers residence detail refetch)
 * @param {Function} options.onCommunityConfigChange - Called when community config version
 *   changes (triggers a refetch of /your_tour_api/config/{slug})
 * @param {boolean} options.enabled - Whether polling is active (default: true)
 */
const useHeartbeat = ( urlSlug, platform, {
  onMapStructureChange,
  onMapOccupancyChange,
  onResidenceVersionChange,
  onCommunityConfigChange,
  enabled = true,
} = {} ) => {
  // Hold the callbacks in a ref so that a caller re-rendering with fresh
  // closures does not tear the shared poller down and rebuild it.
  const handlersRef = useRef( {} );
  handlersRef.current.onMapStructureChange = onMapStructureChange;
  handlersRef.current.onMapOccupancyChange = onMapOccupancyChange;
  handlersRef.current.onResidenceVersionChange = onResidenceVersionChange;
  handlersRef.current.onCommunityConfigChange = onCommunityConfigChange;

  useEffect( () => {
    if ( !urlSlug || !enabled ) {
      return undefined;
    }

    const key = pollerKey( urlSlug, platform );
    let poller = pollers.get( key );
    if ( !poller ) {
      poller = createPoller( urlSlug, platform );
      pollers.set( key, poller );
    }

    const handlers = handlersRef.current;
    poller.subscribers.add( handlers );

    return () => {
      poller.subscribers.delete( handlers );
      if ( poller.subscribers.size === 0 ) {
        pollers.delete( key );
        poller.teardown();
      }
    };
  }, [ urlSlug, platform, enabled ] );
};

export default useHeartbeat;
